/**
 * Swiss Theme Route Configuration
 * 
 * Defines which routes should force specific themes vs allow user control
 * Following next-themes best practices for lightweight theme management
 */

export type Theme = 'clear' | 'swiss';

/**
 * Routes that should ALWAYS use a specific theme (cannot be overridden by user)
 */
export const FORCED_THEME_ROUTES: Record<string, Theme> = {
  '/': 'clear',
  '/about': 'clear',
  '/homev1': 'clear',
} as const;

/**
 * Valid theme values for validation
 */
export const VALID_THEMES: readonly Theme[] = ['clear', 'swiss'] as const;

/**
 * Get the forced theme for a given pathname, if any
 * @param pathname - Current route pathname
 * @returns The forced theme or null if route allows user theme control
 */
export function getForcedTheme(pathname: string): Theme | null {
  return FORCED_THEME_ROUTES[pathname] || null;
}

/**
 * Check if a theme value is valid
 * @param theme - Theme string to validate
 * @returns true if theme is valid
 */
export function isValidTheme(theme: string): theme is Theme {
  return VALID_THEMES.includes(theme as Theme);
}

/**
 * Check if current route allows user theme control
 * @param pathname - Current route pathname
 * @returns true if user can change theme on this route
 */
export function isThemeControlAllowed(pathname: string): boolean {
  return !FORCED_THEME_ROUTES[pathname];
}

/**
 * Get the appropriate theme for a route, considering URL parameters
 * @param pathname - Current route pathname
 * @param urlTheme - Theme from URL parameter
 * @returns The theme that should be applied
 */
export function getRouteTheme(pathname: string, urlTheme: string | null): Theme {
  // Priority 1: Forced theme routes always override
  const forcedTheme = getForcedTheme(pathname);
  if (forcedTheme) {
    return forcedTheme;
  }
  
  // Priority 2: Valid URL parameter on free routes
  if (urlTheme && isValidTheme(urlTheme)) {
    return urlTheme;
  }
  
  // Priority 3: Default to clear theme
  return 'clear';
}

/**
 * Get CSS class for crisis severity based on theme
 * @param severity - Crisis severity level (1-5)
 * @param theme - Current theme
 * @returns CSS class string for severity styling
 */
export function getCrisisSeverityClass(severity: number, theme: Theme): string {
  if (theme === 'swiss') {
    switch (severity) {
      case 1: return 'swiss-severity-low';
      case 2: return 'swiss-severity-minor';
      case 3: return 'swiss-severity-moderate';
      case 4: return 'swiss-severity-major';
      case 5: return 'swiss-severity-critical';
      default: return 'swiss-severity-unknown';
    }
  }
  
  // CLEAR theme (default)
  switch (severity) {
    case 1: return 'bg-green-100 text-green-800';
    case 2: return 'bg-yellow-100 text-yellow-800';
    case 3: return 'bg-orange-100 text-orange-800';
    case 4: return 'bg-red-100 text-red-800';
    case 5: return 'bg-red-200 text-red-900';
    default: return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get CSS class for status based on theme
 * @param status - Status string
 * @param theme - Current theme
 * @returns CSS class string for status styling
 */
export function getSwissStatusClass(status: string, theme: Theme): string {
  if (theme === 'swiss') {
    switch (status.toLowerCase()) {
      case 'active': return 'swiss-status-active';
      case 'monitoring': return 'swiss-status-monitoring';
      case 'resolved': return 'swiss-status-resolved';
      case 'escalated': return 'swiss-status-escalated';
      case 'pending': return 'swiss-status-pending';
      default: return 'swiss-status-default';
    }
  }
  
  // CLEAR theme (default)
  switch (status.toLowerCase()) {
    case 'active': return 'bg-red-100 text-red-800';
    case 'monitoring': return 'bg-yellow-100 text-yellow-800';
    case 'resolved': return 'bg-green-100 text-green-800';
    case 'escalated': return 'bg-purple-100 text-purple-800';
    case 'pending': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Swiss theme utilities object
 */
export const humanitarianUtils = {
  /**
   * Format population count for humanitarian context
   * @param count - Population count
   * @returns Formatted string
   */
  formatPopulationCount: (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  },

  /**
   * Get humanitarian priority class
   * @param priority - Priority level
   * @param theme - Current theme
   * @returns CSS class string
   */
  getPriorityClass: (priority: string, theme: Theme): string => {
    if (theme === 'swiss') {
      switch (priority.toLowerCase()) {
        case 'urgent': return 'swiss-priority-urgent';
        case 'high': return 'swiss-priority-high';
        case 'medium': return 'swiss-priority-medium';
        case 'low': return 'swiss-priority-low';
        default: return 'swiss-priority-default';
      }
    }
    
    // CLEAR theme fallback
    switch (priority.toLowerCase()) {
      case 'urgent': return 'bg-red-200 text-red-900';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }
};

/**
 * Get text class based on variant and theme
 * @param variant - Text variant type
 * @param theme - Current theme
 * @returns CSS class string for text styling
 */
export function getTextClass(variant: 'primary' | 'secondary' | 'tertiary' | 'muted', theme: Theme): string {
  if (theme === 'swiss') {
    switch (variant) {
      case 'primary': return 'swiss-text-primary';
      case 'secondary': return 'swiss-text-secondary';
      case 'tertiary': return 'swiss-text-tertiary';
      case 'muted': return 'swiss-text-muted';
      default: return 'swiss-text-primary';
    }
  }
  
  // CLEAR theme (default)
  switch (variant) {
    case 'primary': return 'text-gray-900';
    case 'secondary': return 'text-gray-600';
    case 'tertiary': return 'text-gray-500';
    case 'muted': return 'text-gray-400';
    default: return 'text-gray-900';
  }
}

/**
 * Get card class based on theme
 * @param theme - Current theme
 * @returns CSS class string for card styling
 */
export function getCardClass(theme: Theme): string {
  if (theme === 'swiss') {
    return 'swiss-card';
  }
  
  // CLEAR theme (default)
  return 'bg-white border border-gray-200 rounded-lg';
}

/**
 * Get button class based on variant and theme
 * @param variant - Button variant type
 * @param theme - Current theme
 * @returns CSS class string for button styling
 */
export function getButtonClass(variant: 'primary' | 'secondary' | 'danger', theme: Theme): string {
  if (theme === 'swiss') {
    switch (variant) {
      case 'primary': return 'swiss-button swiss-button-primary';
      case 'secondary': return 'swiss-button swiss-button-secondary';
      case 'danger': return 'swiss-button swiss-button-danger';
      default: return 'swiss-button swiss-button-primary';
    }
  }
  
  // CLEAR theme (default)
  switch (variant) {
    case 'primary': return 'bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-medium transition-colors';
    case 'secondary': return 'bg-gray-200 hover:bg-gray-300 text-gray-900 px-4 py-2 rounded font-medium transition-colors';
    case 'danger': return 'bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-medium transition-colors';
    default: return 'bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-medium transition-colors';
  }
}

/**
 * Create theme-aware utilities for components
 * @param theme - Current theme
 * @returns Object with theme-aware utility functions
 */
export function createSwissThemeUtils(theme: Theme) {
  return {
    getSeverityClass: (severity: number) => getCrisisSeverityClass(severity, theme),
    getStatusClass: (status: string) => getSwissStatusClass(status, theme),
    getPriorityClass: (priority: string) => humanitarianUtils.getPriorityClass(priority, theme),
    getTextClass: (variant: 'primary' | 'secondary' | 'tertiary' | 'muted' = 'primary') => getTextClass(variant, theme),
    getCardClass: () => getCardClass(theme),
    getButtonClass: (variant: 'primary' | 'secondary' | 'danger' = 'primary') => getButtonClass(variant, theme),
    formatPopulation: humanitarianUtils.formatPopulationCount,
    isSwissTheme: theme === 'swiss',
    isClearTheme: theme === 'clear'
  };
}